import React, { useState, Component } from 'react';
import { XIcon, ClockIcon, PhoneIcon, AlertCircleIcon, SmileIcon, CopyIcon, MessageSquareIcon, CalendarIcon, PackageIcon, TargetIcon, ActivityIcon } from 'lucide-react';
interface Lead {
  id: number;
  name: string;
  initials?: string;
  email: string;
  phone: string;
  callDuration?: string;
  leadType: 'cold' | 'warm' | 'hot';
  status: string;
  dealerCode?: string;
  dealerName?: string;
  carModel?: string;
}
interface LeadDetailOverlayProps {
  isOpen: boolean;
  onClose: () => void;
  lead: Lead | null;
}
// AI Icon Component
function AIIcon({
  className = ''
}: {
  className?: string;
}) {
  return <div className={`w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center ${className}`} data-id="element-736">
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" className="text-blue-600" data-id="element-737">
        <path d="M12 2L15.09 8.26L22 9.27L17 14.14L18.18 21.02L12 17.77L5.82 21.02L7 14.14L2 9.27L8.91 8.26L12 2Z" fill="currentColor" data-id="element-738" />
      </svg>
    </div>;
}
// User Avatar Component
function UserAvatar({
  initials,
  className = ''
}: {
  initials: string;
  className?: string;
}) {
  return <div className={`w-10 h-10 bg-gray-800 rounded-lg flex items-center justify-center text-white text-sm font-bold ${className}`} data-id="element-739">
      {initials}
    </div>;
}
export function LeadDetailOverlay({
  isOpen,
  onClose,
  lead
}: LeadDetailOverlayProps) {
  const [activeTab, setActiveTab] = useState<'details' | 'ai-analytics'>('details');
  const [leadStatus, setLeadStatus] = useState('buy-mahindra-car');
  const [buyStatus, setBuyStatus] = useState('');
  const [notes, setNotes] = useState('');
  const [selectedModel, setSelectedModel] = useState('');
  if (!isOpen || !lead) return null;
  const userInitials = lead.initials || lead.name.substring(0, 2).toUpperCase();
  return <>
      {/* Backdrop */}
      <div className="fixed inset-0 bg-black/20 z-40 transition-opacity duration-300" onClick={onClose} data-id="element-740" />

      {/* Overlay Panel */}
      <div className="fixed right-0 top-0 bottom-0 w-[480px] bg-white shadow-2xl z-50 transform transition-transform duration-300 ease-out flex flex-col" data-id="element-741">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200" data-id="element-742">
          <div className="flex items-center gap-2" data-id="element-743">
            <div className="w-8 h-8 bg-blue-600 rounded flex items-center justify-center" data-id="element-744">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" data-id="element-745">
                <path d="M13 2L3 14h9l-1 8 10-12h-9l1-8z" data-id="element-746" />
              </svg>
            </div>
            <h2 className="text-lg font-semibold text-gray-900" data-id="element-747">
              Lead Details
            </h2>
          </div>
          <div className="flex items-center gap-3" data-id="element-748">
            <button className="px-4 py-2 text-sm font-medium text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors flex items-center gap-2" data-id="element-749">
              <ClockIcon size={16} data-id="element-750" />
              ACTIVITY LOGS
            </button>
            <button onClick={onClose} className="w-8 h-8 flex items-center justify-center text-gray-400 hover:text-gray-600 transition-colors" data-id="element-751">
              <XIcon size={20} data-id="element-752" />
            </button>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex border-b border-gray-200 px-6" data-id="element-753">
          <button onClick={() => setActiveTab('details')} className={`px-4 py-3 text-sm font-semibold border-b-2 transition-colors ${activeTab === 'details' ? 'border-blue-600 text-blue-600' : 'border-transparent text-gray-500 hover:text-gray-700'}`} data-id="element-754">
            DETAILS
          </button>
          <button onClick={() => setActiveTab('ai-analytics')} className={`px-4 py-3 text-sm font-semibold border-b-2 transition-colors ${activeTab === 'ai-analytics' ? 'border-blue-600 text-blue-600' : 'border-transparent text-gray-500 hover:text-gray-700'}`} data-id="element-755">
            AI ANALYTICS
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto px-6 py-6" data-id="element-756">
          {activeTab === 'details' && <div className="space-y-5" data-id="element-757">
              {/* Status Banner */}
              <div className="bg-blue-50 border border-blue-200 rounded-lg px-4 py-3 flex items-center gap-2 text-sm text-blue-700" data-id="element-758">
                <PhoneIcon size={16} data-id="element-759" />
                <span className="font-medium" data-id="element-760">
                  Call routed to dealer and answered.
                </span>
              </div>

              {/* Name and Email */}
              <div className="grid grid-cols-2 gap-4" data-id="element-761">
                <div data-id="element-762">
                  <label className="block text-xs font-medium text-gray-700 mb-2" data-id="element-763">
                    Name
                  </label>
                  <input type="text" value={lead.name} readOnly className="w-full px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg text-sm text-gray-900" data-id="element-764" />
                </div>
                <div data-id="element-765">
                  <label className="block text-xs font-medium text-gray-700 mb-2" data-id="element-766">
                    Email
                  </label>
                  <input type="email" value={lead.email} readOnly className="w-full px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg text-sm text-gray-900" data-id="element-767" />
                </div>
              </div>

              {/* Phone Number, Call Duration, Lead Type */}
              <div className="grid grid-cols-3 gap-4" data-id="element-768">
                <div data-id="element-769">
                  <label className="block text-xs font-medium text-gray-700 mb-2" data-id="element-770">
                    Phone Number
                  </label>
                  <input type="tel" value={lead.phone} readOnly className="w-full px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg text-sm text-gray-900" data-id="element-771" />
                </div>
                <div data-id="element-772">
                  <label className="block text-xs font-medium text-gray-700 mb-2" data-id="element-773">
                    Call Duration
                  </label>
                  <div className="flex items-center gap-2 px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg h-[38px]" data-id="element-774">
                    <ClockIcon size={14} className="text-blue-600" data-id="element-775" />
                    <span className="text-sm font-semibold text-blue-600" data-id="element-776">
                      {lead.callDuration || '12:21 Sec'}
                    </span>
                  </div>
                </div>
                <div data-id="element-777">
                  <label className="block text-xs font-medium text-gray-700 mb-2" data-id="element-778">
                    Lead Type
                  </label>
                  <div className="flex items-center gap-2 px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg h-[38px]" data-id="element-779">
                    <div className="w-4 h-4 bg-blue-500 rounded-full flex items-center justify-center flex-shrink-0" data-id="element-780">
                      <svg width="10" height="10" viewBox="0 0 10 10" fill="none" data-id="element-781">
                        <path d="M2 5L4 7L8 3" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" data-id="element-782" />
                      </svg>
                    </div>
                    <span className="text-sm font-medium text-blue-600" data-id="element-783">
                      Cold
                    </span>
                  </div>
                </div>
              </div>

              {/* Lead Status Radio Buttons */}
              <div data-id="element-784">
                <label className="block text-xs font-medium text-gray-700 mb-3" data-id="element-785">
                  Lead Status
                </label>
                <div className="grid grid-cols-2 gap-3" data-id="element-786">
                  <label className="flex items-center gap-2 cursor-pointer" data-id="element-787">
                    <input type="radio" name="leadStatus" value="buy-mahindra-car" checked={leadStatus === 'buy-mahindra-car'} onChange={e => setLeadStatus(e.target.value)} className="w-4 h-4 text-blue-600 border-gray-300 focus:ring-blue-500" data-id="element-788" />
                    <span className="text-sm text-gray-700 font-medium" data-id="element-789">
                      BUY A MAHINDRA CAR
                    </span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer" data-id="element-790">
                    <input type="radio" name="leadStatus" value="service-mahindra-car" checked={leadStatus === 'service-mahindra-car'} onChange={e => setLeadStatus(e.target.value)} className="w-4 h-4 text-blue-600 border-gray-300 focus:ring-blue-500" data-id="element-791" />
                    <span className="text-sm text-gray-500" data-id="element-792">
                      SERVICE MAHINDRA CAR
                    </span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer" data-id="element-793">
                    <input type="radio" name="leadStatus" value="feedback" checked={leadStatus === 'feedback'} onChange={e => setLeadStatus(e.target.value)} className="w-4 h-4 text-blue-600 border-gray-300 focus:ring-blue-500" data-id="element-794" />
                    <span className="text-sm text-gray-500" data-id="element-795">FEEDBACK</span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer" data-id="element-796">
                    <input type="radio" name="leadStatus" value="non-sale" checked={leadStatus === 'non-sale'} onChange={e => setLeadStatus(e.target.value)} className="w-4 h-4 text-blue-600 border-gray-300 focus:ring-blue-500" data-id="element-797" />
                    <span className="text-sm text-gray-500" data-id="element-798">NON SALE</span>
                  </label>
                </div>
              </div>

              {/* Buy A Mahindra Car Dropdown */}
              <div data-id="element-799">
                <label className="block text-xs font-medium text-gray-700 mb-2" data-id="element-800">
                  Buy A Mahindra Car
                </label>
                <select value={buyStatus} onChange={e => setBuyStatus(e.target.value)} className="w-full px-3 py-2 bg-white border border-gray-300 rounded-lg text-sm text-gray-500 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 appearance-none bg-[url('data:image/svg+xml;charset=UTF-8,%3csvg xmlns=%27http://www.w3.org/2000/svg%27 viewBox=%270 0 24 24%27 fill=%27none%27 stroke=%27currentColor%27 stroke-width=%272%27 stroke-linecap=%27round%27 stroke-linejoin=%27round%27%3e%3cpolyline points=%276 9 12 15 18 9%27%3e%3c/polyline%3e%3c/svg%3e')] bg-[length:20px] bg-[right_0.5rem_center] bg-no-repeat pr-10" data-id="element-801">
                  <option value="" data-id="element-802">Select Status</option>
                  <option value="interested" data-id="element-803">Interested</option>
                  <option value="test-drive" data-id="element-804">Test Drive Scheduled</option>
                  <option value="negotiating" data-id="element-805">Negotiating</option>
                  <option value="closed" data-id="element-806">Closed</option>
                </select>
              </div>

              {/* Notes */}
              <div data-id="element-807">
                <label className="block text-xs font-medium text-gray-700 mb-2" data-id="element-808">
                  Notes
                </label>
                <textarea value={notes} onChange={e => setNotes(e.target.value)} placeholder="Enter Details" rows={3} className="w-full px-3 py-2 bg-white border border-gray-300 rounded-lg text-sm text-gray-700 placeholder-gray-400 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-none" data-id="element-809" />
              </div>

              {/* Sync State Progress */}
              <div className="bg-gray-50 rounded-lg p-4" data-id="element-810">
                <label className="block text-sm font-semibold text-gray-700 mb-4" data-id="element-811">
                  Sync State
                </label>
                <div className="space-y-3" data-id="element-812">
                  <div className="flex items-center justify-between text-[11px] font-semibold" data-id="element-813">
                    <span className="text-gray-600" data-id="element-814">CREATED</span>
                    <span className="text-gray-600" data-id="element-815">OPENED</span>
                    <span className="text-gray-600" data-id="element-816">SAVED</span>
                    <span className="text-blue-600" data-id="element-817">SYNCED</span>
                    <span className="text-gray-400" data-id="element-818">ENQUIRY</span>
                  </div>
                  <div className="flex items-center" data-id="element-819">
                    <div className="flex-1 relative" data-id="element-820">
                      <div className="h-3 bg-blue-600 rounded-l-full" style={{
                    clipPath: 'polygon(0 0, calc(100% - 8px) 0, 100% 50%, calc(100% - 8px) 100%, 0 100%)'
                  }} data-id="element-821"></div>
                    </div>
                    <div className="flex-1 relative -ml-1" data-id="element-822">
                      <div className="h-3 bg-blue-600" style={{
                    clipPath: 'polygon(0 0, calc(100% - 8px) 0, 100% 50%, calc(100% - 8px) 100%, 0 100%, 8px 50%)'
                  }} data-id="element-823"></div>
                    </div>
                    <div className="flex-1 relative -ml-1" data-id="element-824">
                      <div className="h-3 bg-blue-600" style={{
                    clipPath: 'polygon(0 0, calc(100% - 8px) 0, 100% 50%, calc(100% - 8px) 100%, 0 100%, 8px 50%)'
                  }} data-id="element-825"></div>
                    </div>
                    <div className="flex-1 relative -ml-1" data-id="element-826">
                      <div className="h-3 bg-blue-800" style={{
                    clipPath: 'polygon(0 0, calc(100% - 8px) 0, 100% 50%, calc(100% - 8px) 100%, 0 100%, 8px 50%)'
                  }} data-id="element-827"></div>
                    </div>
                    <div className="flex-1 relative -ml-1" data-id="element-828">
                      <div className="h-3 bg-gray-300 rounded-r-full" style={{
                    clipPath: 'polygon(0 0, 100% 0, 100% 100%, 0 100%, 8px 50%)'
                  }} data-id="element-829"></div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 text-xs text-amber-600" data-id="element-830">
                    <AlertCircleIcon size={14} data-id="element-831" />
                    <span data-id="element-832">Syncing Failed on 15 Nov 2024</span>
                  </div>
                </div>
              </div>

              {/* Model Dropdown */}
              <div data-id="element-833">
                <label className="block text-sm font-semibold text-gray-700 mb-2" data-id="element-834">
                  Model
                </label>
                <select value={selectedModel} onChange={e => setSelectedModel(e.target.value)} className="w-full px-3 py-3 bg-white border border-gray-300 rounded-lg text-sm text-gray-500 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 appearance-none bg-[url('data:image/svg+xml;charset=UTF-8,%3csvg xmlns=%27http://www.w3.org/2000/svg%27 viewBox=%270 0 24 24%27 fill=%27none%27 stroke=%27%239ca3af%27 stroke-width=%272%27 stroke-linecap=%27round%27 stroke-linejoin=%27round%27%3e%3cpolyline points=%276 9 12 15 18 9%27%3e%3c/polyline%3e%3c/svg%3e')] bg-[length:20px] bg-[right_0.75rem_center] bg-no-repeat pr-10" data-id="element-835">
                  <option value="" data-id="element-836">Select model</option>
                  <option value="thar" data-id="element-837">Thar</option>
                  <option value="thar-roxx" data-id="element-838">Thar Roxx</option>
                  <option value="scorpio-n" data-id="element-839">Scorpio-N</option>
                  <option value="xuv700" data-id="element-840">XUV700</option>
                  <option value="xuv400" data-id="element-841">XUV400</option>
                  <option value="bolero" data-id="element-842">Bolero</option>
                  <option value="bolero-neo" data-id="element-843">Bolero Neo</option>
                </select>
              </div>

              {/* Lead Created On */}
              <div data-id="element-844">
                <label className="block text-sm font-semibold text-gray-700 mb-2" data-id="element-845">
                  Lead Created on
                </label>
                <input type="text" value="15 Nov 2024" readOnly className="w-full px-3 py-3 bg-gray-50 border border-gray-200 rounded-lg text-sm text-gray-600" data-id="element-846" />
              </div>

              {/* Dealer Code and Dealer Name */}
              <div className="grid grid-cols-2 gap-4" data-id="element-847">
                <div data-id="element-848">
                  <label className="block text-sm font-semibold text-gray-700 mb-2" data-id="element-849">
                    Dealer Code
                  </label>
                  <input type="text" value={lead.dealerCode || 'HR407'} readOnly className="w-full px-3 py-3 bg-gray-50 border border-gray-200 rounded-lg text-sm text-gray-600" data-id="element-850" />
                </div>
                <div data-id="element-851">
                  <label className="block text-sm font-semibold text-gray-700 mb-2" data-id="element-852">
                    Dealer Name
                  </label>
                  <input type="text" value={lead.dealerName || 'Prem Kumar'} readOnly className="w-full px-3 py-3 bg-gray-50 border border-gray-200 rounded-lg text-sm text-gray-600" data-id="element-853" />
                </div>
              </div>
            </div>}

          {activeTab === 'ai-analytics' && <div className="space-y-6" data-id="element-854">
              {/* Call Back Reason Banner */}
              <div className="bg-orange-50 border-l-4 border-orange-400 rounded-r-lg px-4 py-3" data-id="element-855">
                <div className="flex items-center gap-2 text-orange-600 font-semibold text-sm mb-1" data-id="element-856">
                  <PhoneIcon size={16} data-id="element-857" />
                  CALL BACK REASON
                </div>
                <p className="text-gray-700 text-sm" data-id="element-858">
                  Voicebot agent didn't understand user inputs
                </p>
              </div>

              {/* Call Data Section */}
              <div className="bg-gray-50 rounded-xl p-5" data-id="element-859">
                <div className="flex items-center gap-2 mb-4" data-id="element-860">
                  <ActivityIcon size={18} className="text-blue-600" data-id="element-861" />
                  <h3 className="text-sm font-bold text-gray-900 uppercase tracking-wide" data-id="element-862">
                    CALL DATA
                  </h3>
                </div>

                <div className="grid grid-cols-2 gap-6 mb-5" data-id="element-863">
                  <div className="flex items-center gap-3" data-id="element-864">
                    <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center" data-id="element-865">
                      <ClockIcon size={20} className="text-blue-600" data-id="element-866" />
                    </div>
                    <div data-id="element-867">
                      <p className="text-xs text-gray-500 mb-0.5" data-id="element-868">Duration</p>
                      <p className="text-lg font-bold text-gray-900" data-id="element-869">04:32</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3" data-id="element-870">
                    <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center" data-id="element-871">
                      <SmileIcon size={20} className="text-blue-600" data-id="element-872" />
                    </div>
                    <div data-id="element-873">
                      <p className="text-xs text-gray-500 mb-0.5" data-id="element-874">Sentiment</p>
                      <p className="text-lg font-bold text-green-500" data-id="element-875">
                        POSITIVE
                      </p>
                    </div>
                  </div>
                </div>

                <div data-id="element-876">
                  <div className="flex items-center justify-between mb-2" data-id="element-877">
                    <span className="text-sm text-gray-600" data-id="element-878">
                      Call Completion Status
                    </span>
                    <span className="text-sm font-bold text-green-500" data-id="element-879">
                      100%
                    </span>
                  </div>
                  <div className="h-2 bg-gray-200 rounded-full overflow-hidden" data-id="element-880">
                    <div className="h-full bg-green-500 rounded-full" style={{
                  width: '100%'
                }} data-id="element-881"></div>
                  </div>
                </div>
              </div>

              {/* Call Intent Section */}
              <div className="border-b border-gray-200 pb-6" data-id="element-882">
                <div className="flex items-center gap-2 mb-4" data-id="element-883">
                  <TargetIcon size={18} className="text-blue-600" data-id="element-884" />
                  <h3 className="text-sm font-bold text-gray-900 uppercase tracking-wide" data-id="element-885">
                    CALL INTENT
                  </h3>
                </div>

                <div className="grid grid-cols-2 gap-6" data-id="element-886">
                  <div data-id="element-887">
                    <p className="text-xs text-blue-500 mb-2" data-id="element-888">Reason</p>
                    <span className="inline-block px-4 py-1.5 bg-orange-50 text-orange-600 border border-orange-200 rounded-full text-sm font-medium" data-id="element-889">
                      Purchase Intent
                    </span>
                  </div>
                  <div data-id="element-890">
                    <p className="text-xs text-blue-500 mb-2" data-id="element-891">Purpose</p>
                    <p className="text-sm text-gray-700" data-id="element-892">
                      Inquiring about new SUV models and test drive
                      availability.
                    </p>
                  </div>
                </div>
              </div>

              {/* Product Interest Section */}
              <div className="border-b border-gray-200 pb-6" data-id="element-893">
                <div className="flex items-center gap-2 mb-4" data-id="element-894">
                  <PackageIcon size={18} className="text-blue-600" data-id="element-895" />
                  <h3 className="text-sm font-bold text-gray-900 uppercase tracking-wide" data-id="element-896">
                    PRODUCT INTEREST
                  </h3>
                </div>

                <div className="grid grid-cols-2 gap-6 mb-4" data-id="element-897">
                  <div data-id="element-898">
                    <p className="text-xs text-blue-500 mb-2" data-id="element-899">Product</p>
                    <p className="text-sm font-medium text-gray-900" data-id="element-900">
                      Tata Harrier XZA+
                    </p>
                  </div>
                  <div data-id="element-901">
                    <p className="text-xs text-blue-500 mb-2" data-id="element-902">Budget</p>
                    <p className="text-sm font-medium text-gray-900" data-id="element-903">
                      ₹20L - ₹25L
                    </p>
                  </div>
                </div>

                <div data-id="element-904">
                  <p className="text-xs text-blue-500 mb-2" data-id="element-905">Preferences</p>
                  <div className="flex flex-wrap gap-2" data-id="element-906">
                    {['Dark Edition', 'Automatic', 'Sunroof', 'Diesel'].map(pref => <span key={pref} className="px-3 py-1.5 bg-white border border-gray-300 rounded-full text-sm text-gray-700" data-id="element-907">
                          {pref}
                        </span>)}
                  </div>
                </div>
              </div>

              {/* Timeline / Preferences Section */}
              <div className="border-b border-gray-200 pb-6" data-id="element-908">
                <div className="flex items-center gap-2 mb-4" data-id="element-909">
                  <CalendarIcon size={18} className="text-blue-600" data-id="element-910" />
                  <h3 className="text-sm font-bold text-gray-900 uppercase tracking-wide" data-id="element-911">
                    TIMELINE / PREFERENCES
                  </h3>
                </div>

                <div className="grid grid-cols-2 gap-x-6 gap-y-4" data-id="element-912">
                  <div data-id="element-913">
                    <p className="text-xs text-blue-500 mb-1" data-id="element-914">
                      Expected Purchase
                    </p>
                    <p className="text-sm font-medium text-gray-900" data-id="element-915">
                      Within 2 weeks
                    </p>
                  </div>
                  <div data-id="element-916">
                    <p className="text-xs text-blue-500 mb-1" data-id="element-917">
                      Conversion Score
                    </p>
                    <p className="text-sm font-medium text-gray-900" data-id="element-918">High</p>
                  </div>
                  <div data-id="element-919">
                    <p className="text-xs text-blue-500 mb-1" data-id="element-920">Contact Time</p>
                    <p className="text-sm font-medium text-gray-900" data-id="element-921">
                      After 6:00 PM
                    </p>
                  </div>
                  <div data-id="element-922">
                    <p className="text-xs text-blue-500 mb-1" data-id="element-923">Appointment</p>
                    <p className="text-sm font-medium text-gray-900" data-id="element-924">
                      Scheduled: Sat, 06th Dec, 11:00 AM
                    </p>
                  </div>
                  <div data-id="element-925">
                    <p className="text-xs text-blue-500 mb-1" data-id="element-926">Channel</p>
                    <p className="text-sm font-medium text-gray-900" data-id="element-927">
                      WhatsApp
                    </p>
                  </div>
                </div>
              </div>

              {/* Question / Conversation by Voicebot Agent */}
              <div data-id="element-928">
                <div className="flex items-center gap-2 mb-4" data-id="element-929">
                  <MessageSquareIcon size={18} className="text-blue-600" data-id="element-930" />
                  <h3 className="text-sm font-bold text-gray-900 uppercase tracking-wide" data-id="element-931">
                    QUESTION / CONVERSATION BY VOICEBOT AGENT
                  </h3>
                </div>

                <div className="space-y-4" data-id="element-932">
                  {/* Name Question */}
                  <div className="flex items-start gap-3" data-id="element-933">
                    <AIIcon data-id="element-934" />
                    <p className="text-sm text-gray-700 pt-2.5" data-id="element-935">Name?</p>
                  </div>
                  <div className="flex items-start gap-3" data-id="element-936">
                    <UserAvatar initials={userInitials} data-id="element-937" />
                    <div className="pt-2" data-id="element-938">
                      <p className="text-sm text-gray-900" data-id="element-939">{lead.name}.</p>
                      <button className="text-gray-400 hover:text-gray-600 mt-1" data-id="element-940">
                        <CopyIcon size={14} data-id="element-941" />
                      </button>
                    </div>
                  </div>

                  {/* Email Question */}
                  <div className="flex items-start gap-3" data-id="element-942">
                    <AIIcon data-id="element-943" />
                    <p className="text-sm text-gray-700 pt-2.5" data-id="element-944">Email?</p>
                  </div>
                  <div className="flex items-start gap-3" data-id="element-945">
                    <UserAvatar initials={userInitials} data-id="element-946" />
                    <div className="pt-2" data-id="element-947">
                      <p className="text-sm text-gray-900" data-id="element-948">{lead.email}</p>
                      <button className="text-gray-400 hover:text-gray-600 mt-1" data-id="element-949">
                        <CopyIcon size={14} data-id="element-950" />
                      </button>
                    </div>
                  </div>

                  {/* Model Interested Question */}
                  <div className="flex items-start gap-3" data-id="element-951">
                    <AIIcon data-id="element-952" />
                    <p className="text-sm text-gray-700 pt-2.5" data-id="element-953">
                      Model Interested?
                    </p>
                  </div>
                  <div className="flex items-start gap-3" data-id="element-954">
                    <UserAvatar initials={userInitials} data-id="element-955" />
                    <div className="pt-2" data-id="element-956">
                      <p className="text-sm text-gray-900" data-id="element-957">
                        {lead.carModel || 'Scorpio-N'}
                      </p>
                      <button className="text-gray-400 hover:text-gray-600 mt-1" data-id="element-958">
                        <CopyIcon size={14} data-id="element-959" />
                      </button>
                    </div>
                  </div>

                  {/* Test Drive Question */}
                  <div className="flex items-start gap-3" data-id="element-960">
                    <AIIcon data-id="element-961" />
                    <p className="text-sm text-gray-700 pt-2.5" data-id="element-962">Test Drive?</p>
                  </div>
                  <div className="flex items-start gap-3" data-id="element-963">
                    <UserAvatar initials={userInitials} data-id="element-964" />
                    <div className="pt-2" data-id="element-965">
                      <p className="text-sm text-gray-500 italic" data-id="element-966">
                        Not Answered...
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>}
        </div>

        {/* Footer Actions */}
        <div className="border-t border-gray-200 px-6 py-4 flex items-center justify-between bg-white" data-id="element-967">
          <button className="px-6 py-2.5 text-sm font-semibold text-blue-600 border-2 border-blue-600 rounded-lg hover:bg-blue-50 transition-colors" data-id="element-968">
            SAVE LEAD
          </button>
          <button className="px-6 py-2.5 text-sm font-semibold text-white bg-blue-600 rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-2 shadow-sm" data-id="element-969">
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none" data-id="element-970">
              <path d="M8 2v12M2 8h12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" data-id="element-971" />
            </svg>
            SYNC & SAVE
          </button>
        </div>
      </div>
    </>;
}